import {ApplySuggestionDialog} from '@github-ui/diff-lines/apply-suggestion-dialog'
import type {SuggestionDetails, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {DiscardBatchConfirmationDialog} from '@github-ui/diff-lines/discard-batch-confirmation-dialog'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {Button, CounterLabel, Stack, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useRef, useState} from 'react'
import {useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {getSuggestionDetails} from '../../helpers/get-suggestion-details'
import {clearActiveBatchFromStorage, saveActiveBatchToStorage} from '../../helpers/suggestion-batch-storage'
import {useHeaderLiveUpdates} from '../../hooks/use-header-live-updates'
import {useMergeStatusButtonData} from '../../hooks/use-merge-status-button-data'
import {useApplyBatchedSuggestionsMutation} from '../../mutations/use-apply-batched-suggestions-mutation'
import {useUpdateShowChangeGroupsPreferenceMutation} from '../../mutations/use-update-show-change-groups-preference-mutation'
import {useUpdateWhitespacePreferenceMutation} from '../../mutations/use-update-whitespace-preference-mutation'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import type {FilesPageNotices, FilesRoutePayload, PageLimits, UserNotice} from '../../page-data/payloads/files'
import type {AliveChannels, MergeStatusButtonData} from '../../page-data/payloads/header'
import {
  useAllMarkers,
  useComparisonInfo,
  useIsSingleFileMode,
  useIsSuggestionBatchPending,
  usePullRequestBasePath,
  useShowChangeGroupsPreference,
  useSuggestionBatch,
  useSuggestionBatchActions,
} from '../../stores/selectors'
import {ChangesSelector} from '../diff-filtering/ChangesSelector'
import {PullRequestHeaderSummary} from '../PullRequestHeaderSummary'
import {PullRequestStateLabel} from '../PullRequestStateLabel'
import {ChangeGroupsPreferences} from './ChangeGroupsPreferences'
import {OpenAgentsPanelButton} from './OpenAgentsPanelButton'
import {OpenAlertsPanelButton} from './OpenAlertsPanelButton'
import {OpenCommentsPanelButton} from './OpenCommentsPanelButton'
import {OpenOverviewPanelButton} from './OpenOverviewPanelButton'
import styles from './PullRequestFilesToolbar.module.css'
import {RefreshButton} from './RefreshButton'
import {ReviewMenuButton} from './ReviewMenuButton'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {ToolbarMoreMenu} from './ToolbarMoreMenu'
import {ViewedFileProgress} from './ViewedFileProgress'
import {OpenCopilotChatPanelButton} from './OpenCopilotChatPanelButton'

export type PullRequestFilesToolbarProps = {
  changeGroupsPreferenceAvailable: boolean
  commits: FilesRoutePayload['commits']
  currentUserLogin?: string
  isFileTreeExpanded?: boolean
  lastReviewOid?: string
  mergeStatusButtonData?: MergeStatusButtonData
  pageLimits: PageLimits
  pullRequest: FilesRoutePayload['pullRequest']
  aliveChannels: AliveChannels
  repository: FilesRoutePayload['repository']
  shouldShowViewedFilesCount: boolean
  stackBaseBranch?: string
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
  userNotices?: Array<UserNotice<FilesPageNotices>>
}

export function LivePullRequestFilesToolbar({
  repository,
  pullRequest: initialPullRequest,
  userNotices,
  ...pullRequestFilesToolbarProps
}: Omit<PullRequestFilesToolbarProps, 'aliveChannels' | 'mergeStatusButtonData'>) {
  const {
    data: {pullRequest, aliveChannels, mergeStatusButtonData, stack},
  } = useHeaderPageData()
  useHeaderLiveUpdates(aliveChannels)

  // Preserve viewerAllowedNonCommentReviewTypes from the initial payload. This property
  // is being lifted into the header data behind a feature flag for deploy safety, so we
  // don't want the header response to overwrite the initial value until rollout is complete.
  // See https://github.com/github/github/pull/429353 for more details. This can be removed
  // when the updated_pull_request_header feature flag is removed.
  const updatedPullRequest = {
    ...initialPullRequest,
    ...pullRequest,
    viewerAllowedNonCommentReviewTypes: initialPullRequest.viewerAllowedNonCommentReviewTypes,
  } as FilesRoutePayload['pullRequest']

  return (
    <PullRequestFilesToolbar
      repository={repository}
      pullRequest={updatedPullRequest}
      aliveChannels={aliveChannels}
      mergeStatusButtonData={mergeStatusButtonData}
      stackBaseBranch={stack?.baseBranch}
      userNotices={userNotices}
      {...pullRequestFilesToolbarProps}
    />
  )
}

export function PullRequestFilesToolbar({
  changeGroupsPreferenceAvailable,
  commits,
  currentUserLogin,
  isFileTreeExpanded,
  lastReviewOid,
  mergeStatusButtonData,
  pageLimits,
  pullRequest,
  aliveChannels,
  repository,
  shouldShowViewedFilesCount,
  stackBaseBranch,
  totalFilesCount,
  treeToggleElement,
  userNotices,
}: PullRequestFilesToolbarProps) {
  const {mutate: updateWhitespace} = useUpdateWhitespacePreferenceMutation()
  const {clearSuggestionBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()
  const {mutate: applyBatchedSuggestions} = useApplyBatchedSuggestionsMutation(
    pullRequest.pathName,
    pullRequest.comparison.headOid,
  )
  const {mutate: updateChangeGroupPreference} = useUpdateShowChangeGroupsPreferenceMutation()
  const showChangeGroups = useShowChangeGroupsPreference()
  const [isSticky, setIsSticky] = useState(false)
  // Display the divider only if our new feature is DISABLED AND one of the following is true
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const showDivider = !showUpdatedHeader && (shouldShowViewedFilesCount || !isFileTreeExpanded)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const isSuggestionBatchPending = useIsSuggestionBatchPending()
  const suggestionBatch = useSuggestionBatch()
  const markers = useAllMarkers()
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false)
  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const [changesSelectorOpen, setChangesSelectorOpen] = useState(false)

  const suggestionDetails = getSuggestionDetails(markers, suggestionBatch.activeBatch)
  const suggestionsCount = suggestionDetails.length

  const onUpdateWhitespace = (updatedValue: boolean) => {
    updateWhitespace({hideWhitespace: updatedValue})
  }

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  const handleRemoveSuggestion = useCallback(
    (suggestionId: SuggestionIdentifier) => {
      removeSuggestionFromBatch(suggestionId)
      saveActiveBatchToStorage(
        pullRequestBasePath,
        comparisonInfo.fullDiff.headOid,
        suggestionBatch.activeBatch.filter(
          id =>
            !(
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex
            ),
        ),
      )
    },
    [removeSuggestionFromBatch, suggestionBatch, pullRequestBasePath, comparisonInfo],
  )

  const handleCommitBatch = useCallback(() => {
    setIsCommitDialogOpen(true)
  }, [])

  const handleCommitDialogClose = useCallback(() => {
    setIsCommitDialogOpen(false)
  }, [])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const handleDiscardBatch = useCallback(() => {
    clearSuggestionBatch()
    clearActiveBatchFromStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid)
    handleCommitDialogClose()
    setShowDiscardConfirmation(false)
  }, [clearSuggestionBatch, pullRequestBasePath, comparisonInfo, handleCommitDialogClose])

  const handleCommit = useCallback(
    (commitMessage: string, onError: (error: Error) => void, onCompleted: () => void) => {
      applyBatchedSuggestions(
        {message: commitMessage},
        {
          onSuccess: () => {
            handleCommitDialogClose()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error)
          },
        },
      )
    },
    [applyBatchedSuggestions, handleCommitDialogClose],
  )
  const onShowChangeGroupsChange = (newVal: boolean) => {
    updateChangeGroupPreference({showChangeGroups: newVal})
  }

  const suggestions = suggestionDetails.map(
    (detail): SuggestionDetails => ({
      commentDatabaseId: detail.commentDatabaseId,
      suggestionIndex: detail.suggestionIndex,
      path: detail.path,
      bodyHTML: detail.bodyHTML,
      diffEntries: detail.diffEntries,
    }),
  )

  const sidePanelOpen = useIsDockedPanelOpen()

  // Merge status button setup
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const shouldShowMergeStatusButton = !showUpdatedHeader && validatedMergeData && isSticky && !sidePanelOpen
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel('mergeStatus', {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isCodingAgentLogsInPrEnabled = isFeatureEnabled('coding_agent_logs_in_pr')
  const renderAgentSidePanelButton = isCodingAgentLogsInPrEnabled

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(
          stickyStyles.stickyHeader,
          styles.toolbar,
          isSticky && styles['is-stuck'],
          showUpdatedHeader && styles.updatedHeaderToolbar,
        )}
        data-side-panel-open={sidePanelOpen}
        data-file-tree-expanded={isFileTreeExpanded}
      >
        <h2 className="sr-only">Pull Request Toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
          {/* (Mini header already shows this) */}
          {!showUpdatedHeader && (
            <div className={styles['show-when-stuck']}>
              <PullRequestStateLabel state={pullRequest.state} />
            </div>
          )}
          <div
            className={clsx(
              // Only hide if NOT using updated header
              !showUpdatedHeader && styles['hide-when-stuck-large'],
              !showUpdatedHeader &&
                (isFileTreeExpanded
                  ? styles['hide-commit-selector-on-small']
                  : styles['hide-commit-selector-on-medium']),
            )}
          >
            <ChangesSelector
              commits={commits}
              lastReviewOid={lastReviewOid}
              ownerLogin={repository.ownerLogin}
              pullRequestNumber={pullRequest.number}
              repositoryName={repository.name}
              buttonClassName={styles['hide-icon-on-small']}
            />
          </div>
          {/* Only show PR title/summary when NOT using updated header (mini header already shows this) */}
          {!showUpdatedHeader && (
            <Stack direction="vertical" gap="none" className={clsx(styles['show-when-stuck'], 'min-width-0')}>
              <div className={clsx('d-flex mb-n1', styles['pr-title-details'])}>
                <Tooltip type="label" direction="s" text={pullRequest.title}>
                  <a href="#top" className="d-flex overflow-hidden fgColor-default">
                    <bdi className={clsx('f5 text-bold overflow-hidden no-wrap', styles['pr-sticky-title'])}>
                      {pullRequest.title}
                    </bdi>
                  </a>
                </Tooltip>
                <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
              </div>

              <div
                className={clsx(
                  'f6 text-normal d-flex flex-items-center ml-n2',
                  isFileTreeExpanded
                    ? styles['hide-commit-selector-on-small']
                    : styles['hide-commit-selector-on-medium'],
                )}
              >
                <ChangesSelector
                  commits={commits}
                  lastReviewOid={lastReviewOid}
                  ownerLogin={repository.ownerLogin}
                  pullRequestNumber={pullRequest.number}
                  repositoryName={repository.name}
                  variant="condensed"
                  open={changesSelectorOpen}
                  onOpenChange={setChangesSelectorOpen}
                />
                <div className={clsx('d-none d-lg-flex flex-items-center min-width-0', styles['pr-title-details'])}>
                  <div className="border-left mx-1 pr-1 d-block" style={{width: '1px', height: '16px'}} />
                  <PullRequestHeaderSummary
                    author={pullRequest.author}
                    baseBranch={pullRequest.baseBranch}
                    baseRepositoryOwnerLogin={repository.ownerLogin}
                    baseRepositoryName={repository.name}
                    commitsCount={pullRequest.commitsCount}
                    headBranch={pullRequest.headBranch}
                    headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                    headRepositoryName={pullRequest.headRepositoryName}
                    isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                    mergedBy={pullRequest.mergedBy}
                    mergedByName={pullRequest.mergedByName}
                    state={pullRequest.state}
                    pullRequestId={pullRequest.id}
                    pullRequestNumber={pullRequest.number}
                    repositoryId={repository.id}
                  />
                </div>
              </div>
            </Stack>
          )}
        </Stack>
        <Stack direction="horizontal" align="center">
          {!showUpdatedHeader && <RefreshButton aliveChannel={pullRequest.aliveChannel} />}

          <div className={clsx('d-flex', styles['file-controls'])}>
            {shouldShowViewedFilesCount && (
              <ViewedFileProgress
                totalFilesCount={totalFilesCount}
                className={styles['hide-viewed-progress-on-small']}
              />
            )}

            {isSingleFileMode && totalFilesCount > 1 && (
              <div
                className={clsx(
                  styles['file-controls-navigation'],
                  showUpdatedHeader && styles['hide-file-controls-on-small'],
                )}
              >
                <ReviewNavigationButtonGroup userNotices={userNotices} />
              </div>
            )}
          </div>
          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}
          {isSuggestionBatchPending ? (
            <>
              <Button aria-label="Discard batch" variant="danger" size="small" onClick={handleDiscardClick}>
                Discard<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> batch</span>
              </Button>
              <Button aria-label="Commit suggestions" variant="primary" size="small" onClick={handleCommitBatch}>
                Commit<span className={`d-none ${sidePanelOpen ? ' d-xl-inline' : ' d-lg-inline'}`}> suggestions</span>{' '}
                <CounterLabel className="ml-1 color-fg-inherit">{suggestionsCount}</CounterLabel>
              </Button>
            </>
          ) : (
            <>
              {shouldShowMergeStatusButton && (
                <div className={styles['show-on-xlarge']}>
                  <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
                    <MergeStatusButtonWrapper
                      channels={validatedMergeData.channels}
                      headSha={validatedMergeData.headSha}
                      helpUrl={validatedMergeData.helpUrl}
                      viewerLogin={validatedMergeData.viewerLogin}
                      isDockedMode
                      onToggleDockedPanel={handleToggleDockedMergePanel}
                      mergeStatusButtonRef={mergeStatusButtonRef}
                      size="small"
                    />
                  </MergeMethodContextProvider>
                </div>
              )}
              {currentUserLogin && !showUpdatedHeader && (
                <ReviewMenuButton
                  currentUserLogin={currentUserLogin}
                  headRefOid={pullRequest.comparison.headOid}
                  pullRequest={pullRequest}
                  repository={repository}
                  stackBaseBranch={isFeatureEnabled('pull_request_stacks') ? stackBaseBranch : undefined}
                />
              )}
              <div className={styles['container-show-on-sm']}>
                <DiffViewSettings
                  ariaLabel={showUpdatedHeader ? 'Diff settings' : undefined}
                  invisible={showUpdatedHeader}
                  reloadOnWhitespaceChange
                  onUpdateWhitespace={onUpdateWhitespace}
                  defaultHideWhitespace={comparisonInfo.hideWhitespace}
                  small
                  userNotices={userNotices}
                  additionalPreferences={
                    <ChangeGroupsPreferences
                      changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                      showChangeGroups={showChangeGroups}
                      onShowChangeGroupsChange={onShowChangeGroupsChange}
                    />
                  }
                />
              </div>
            </>
          )}
          {!showUpdatedHeader && (
            <>
              <div className={styles['file-controls-divider']} />
              <div className="d-flex gap-1">
                <OpenOverviewPanelButton aliveChannel={pullRequest.aliveChannel} />
                {renderAgentSidePanelButton && (
                  <div className={styles['container-show-on-sm']}>
                    <OpenAgentsPanelButton repository={repository} pullRequestNumber={pullRequest.number} />
                  </div>
                )}
                <div className={styles['container-show-on-sm']}>
                  <OpenCommentsPanelButton pageLimits={pageLimits} aliveChannels={aliveChannels} />
                </div>
                <OpenAlertsPanelButton
                  buttonClasses="d-none d-sm-flex"
                  hideIfNoAlerts
                  pageLimits={pageLimits}
                  endCommitOid={pullRequest.comparison.headOid}
                />
                {isFeatureEnabled('copilot_chat_docked_panel') && <OpenCopilotChatPanelButton />}
              </div>
            </>
          )}
          <div className={isFileTreeExpanded ? styles['show-more-menu-on-small'] : styles['show-more-menu-on-medium']}>
            <ToolbarMoreMenu
              pageLimits={pageLimits}
              pullRequest={pullRequest}
              aliveChannels={aliveChannels}
              hideWhitespace={comparisonInfo.hideWhitespace}
              onUpdateWhitespace={onUpdateWhitespace}
              showCommitSelector={() => setChangesSelectorOpen(true)}
              commits={commits}
              isFileTreeExpanded={isFileTreeExpanded}
              repository={repository}
              pullRequestNumber={pullRequest.number}
              additionalPreferences={
                <ChangeGroupsPreferences
                  changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                  showChangeGroups={showChangeGroups}
                  onShowChangeGroupsChange={onShowChangeGroupsChange}
                />
              }
            />
          </div>
        </Stack>
      </Stack>
      {isCommitDialogOpen && suggestions.length > 0 && (
        <ApplySuggestionDialog
          authorLogins={[]}
          batchSize={suggestionBatch.activeBatch.length}
          onClose={handleCommitDialogClose}
          onCommit={handleCommit}
          defaultCommitMessage="Apply suggestions from code review"
          suggestions={suggestions}
          onRemoveSuggestion={handleRemoveSuggestion}
          onDiscardBatch={handleDiscardBatch}
        />
      )}
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardBatch}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestionsCount}
      />
    </>
  )
}
